/**
 * WCO-3286 — format admin audit before/after snapshots for display.
 */
import { formatTimestamp } from '../../../utils/dateUtils';

/** Omit noisy metadata from the primary diff list (still auditable via raw API). */
export const AUDIT_HIDDEN_FIELD_KEYS = new Set(['created_at', 'updated_at']);

const TIMESTAMP_FIELD_RE = /(_at|_date|_time)$/i;

export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

function isBooleanLike(value: unknown): boolean {
  if (typeof value === 'boolean') return true;
  if (value === 'Y' || value === 'N') return true;
  if (value === 'y' || value === 'n') return true;
  return false;
}

function toBooleanDisplay(value: unknown, t: (key: string) => string): string {
  if (value === true || value === 'Y' || value === 'y') return t('common.yes');
  if (value === false || value === 'N' || value === 'n') return t('common.no');
  return String(value);
}

export function formatAuditFieldValue(
  fieldKey: string,
  value: unknown,
  t: (key: string) => string,
): string {
  if (value === null || value === undefined) return '—';
  if (isBooleanLike(value)) return toBooleanDisplay(value, t);
  if (TIMESTAMP_FIELD_RE.test(fieldKey) && typeof value === 'string') {
    return formatTimestamp(value);
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/**
 * Friendly label for an audited field.
 *
 * `t()` returns the KEY ITSELF when a translation is missing, which is truthy — so a plain
 * `t(key) || fallback` never falls back and the UI prints `adminActivity.fields.camera_url`.
 * Compare against the lookup key, then humanise (`warehouse_code` → "Warehouse code",
 * `passwordReset` → "Password reset") so an unlabelled field still reads as words.
 */
export function auditFieldLabel(fieldKey: string, t: (key: string) => string): string {
  const lookupKey = `adminActivity.fields.${fieldKey}`;
  const translated = t(lookupKey);
  if (translated && translated !== lookupKey) return translated;

  const words = fieldKey
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface AuditDiffRow {
  key: string;
  label: string;
  beforeDisplay: string | null;
  afterDisplay: string | null;
  changed: boolean;
}

export function buildAuditDiffRows(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  t: (key: string) => string,
): AuditDiffRow[] {
  const beforeObj = before ?? {};
  const afterObj = after ?? {};
  const keys = new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]);

  const rows: AuditDiffRow[] = [];
  for (const key of [...keys].sort()) {
    if (AUDIT_HIDDEN_FIELD_KEYS.has(key)) continue;

    const beforeVal = key in beforeObj ? beforeObj[key] : undefined;
    const afterVal = key in afterObj ? afterObj[key] : undefined;
    const hasBefore = key in beforeObj;
    const hasAfter = key in afterObj;
    const changed = hasBefore && hasAfter && !valuesEqual(beforeVal, afterVal);
    const createdOnly = !hasBefore && hasAfter;
    const deletedOnly = hasBefore && !hasAfter;

    if (hasBefore && hasAfter && !changed && !createdOnly && !deletedOnly) {
      continue;
    }

    if (!hasBefore && !hasAfter) continue;

    const label = auditFieldLabel(key, t);
    rows.push({
      key,
      label,
      beforeDisplay: hasBefore ? formatAuditFieldValue(key, beforeVal, t) : null,
      afterDisplay: hasAfter ? formatAuditFieldValue(key, afterVal, t) : null,
      changed: changed || createdOnly || deletedOnly,
    });
  }
  return rows;
}

export function formatPickerDateLabel(dateYmd: string): string {
  const d = new Date(`${dateYmd}T00:00:00`);
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })}`;
}
